<template>
  <v-card
    variant="flat"
    class="mb-4 pa-6"
  >
    <h3 class="text-h6 mb-2">
      Document Status
    </h3>
    <p class="text-body-1 text-medium-emphasis mb-8">
      Document signing requests have been sent to the specified signing authority. Track status below.
    </p>

    <v-progress-circular
      v-if="isLoading"
      indeterminate
      color="primary"
      class="ma-4"
    />

    <v-list
      v-else
      class="document-list"
    >
      <v-list-item
        v-for="doc in documents"
        :key="doc.id"
        :class="{ 'completed': doc.status === 'signed' }"
        class="mb-4"
      >
        <!-- <template #prepend>
          <div class="status-icon">
            <v-icon
              :icon="getStatusIcon(doc.status)"
              :color="getStatusColor(doc.status)"
              size="24"
            />
          </div>
        </template> -->

        <v-list-item-title class="text-subtitle-1 font-weight-medium mb-2">
          {{ doc.name }}
        </v-list-item-title>
        
        <v-list-item-subtitle class="d-flex flex-column">
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="d-flex flex-column">
              <span class="text-caption text-medium-emphasis d-flex align-center">
                <v-icon
                  icon="mdi-account-outline"
                  size="16"
                  class="me-1"
                />
                {{ doc.signer.name }} ({{ doc.signer.title }})
              </span>
              <span class="text-caption text-medium-emphasis mt-1 d-flex align-center">
                <v-icon
                  icon="mdi-email-outline"
                  size="16"
                  class="me-1"
                />
                {{ doc.signer.email }}
              </span>
            </div>
            <v-chip
              :color="getStatusColor(doc.status)"
              size="small"
              :prepend-icon="getStatusIcon(doc.status)"
              variant="outlined"
              class="font-weight-medium text-high-emphasis"
            >
              {{ getStatusText(doc.status) }}
            </v-chip>
          </div>
          <span 
            v-if="doc.lastUpdated"
            class="text-caption text-medium-emphasis d-flex align-center"
          >
            <v-icon
              icon="mdi-clock-outline"
              size="16"
              class="me-1"
            />
            Last Updated {{ formatDate(doc.lastUpdated) }}
          </span>
        </v-list-item-subtitle>
      </v-list-item>
    </v-list>
  </v-card>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { format } from 'date-fns'
import { useLegalDocumentStore } from '@/stores/legal_document'
import type { ISignableDocument } from '@/models/legal_documents'

type DocumentStatus = 'sent' | 'signed'

interface DocumentDisplay {
  id?: number
  name: string
  status: DocumentStatus
  lastUpdated?: Date
  signer: {
    name: string
    email: string
    title: string
  }
}

const legalDocumentStore = useLegalDocumentStore()
const isLoading = ref(true)

onMounted(async () => {
  try {
    await legalDocumentStore.fetchSignableDocuments()
  } finally {
    isLoading.value = false
  }
})

const documents = computed<DocumentDisplay[]>(() => {
  return legalDocumentStore.getSignableDocuments.map((doc: ISignableDocument) => ({
    id: doc.id,
    name: doc.title,
    status: doc.is_signed ? 'signed' : 'sent',
    lastUpdated: doc.modified || doc.created,
    signer: {
      name: doc.signer_name,
      email: doc.signer_email,
      title: doc.signer_title
    }
  }))
})

function getStatusIcon(status: DocumentStatus) {
  return status === 'signed' 
    ? 'mdi-check-circle-outline'
    : 'mdi-file-document-edit-outline'
}

function getStatusColor(status: DocumentStatus) {
  return status === 'signed' 
    ? 'success'
    : 'primary'
}

function getStatusText(status: DocumentStatus) {
  return status === 'signed' 
    ? 'Signed'
    : 'Waiting for signature'
}

function formatDate(date: Date) {
  return format(date, 'MMM d, yyyy h:mm a')
}
</script>

<style scoped>
.document-list {
  background: transparent;
  padding: 0;
}

.v-list-item {
  border-radius: 12px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  transition: all 0.2s ease;
  padding: 16px;
  background: rgb(var(--v-theme-surface));

  &:hover {
    border-color: rgb(var(--v-theme-primary));
  }

  &.completed {
    background: rgb(var(--v-theme-surface));
    border-color: rgb(var(--v-theme-success));

    &:hover {
      border-color: rgb(var(--v-theme-success-darken-1));
    }
  }

  :deep(.v-list-item__overlay) {
    display: none;
  }
}

.status-icon {
  background: transparent;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
}

.status-badge {
  border-radius: 16px;
  background: rgb(var(--v-theme-primary-lighten-1));
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
  letter-spacing: 0.0125em;

  &.signed {
    background: rgb(var(--v-theme-success-lighten-1));
    color: rgb(var(--v-theme-success));
  }
}
</style> 